import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #scrollYAtOpen = null;

  /** @type {ScrollRestoration | null} */
  #prevScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleDiscountUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleDiscountUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#restoreScrollBehavior();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // The mobile back-button-closes-drawer UX below fires a popstate on close, and the
    // browser's automatic scroll restoration then resets the page to the TOP. Take manual
    // control of scroll restoration for the drawer's lifetime and remember where the page
    // was, so closing the drawer leaves the page exactly where the user left it.
    this.#scrollYAtOpen = window.scrollY;
    if ('scrollRestoration' in history) {
      this.#prevScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
    // Restore the page's scroll position after the history pop settles, then hand
    // scroll restoration back to the browser.
    requestAnimationFrame(() => this.#restoreScrollBehavior());
  };

  #restoreScrollBehavior() {
    if (this.#scrollYAtOpen !== null) {
      window.scrollTo({ top: this.#scrollYAtOpen, left: 0, behavior: 'instant' });
      this.#scrollYAtOpen = null;
    }
    if (this.#prevScrollRestoration !== null && 'scrollRestoration' in history) {
      history.scrollRestoration = this.#prevScrollRestoration;
      this.#prevScrollRestoration = null;
    }
  }

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // Never auto-open the header drawer while ON the cart page: the page manages its own
    // cart UI, so qty +/-, deletes, and delete-last there must not pop the drawer (or its
    // empty state) over the page. Announce-only in that case.
    const onCartPage = document.querySelector('.cart-page') !== null;
    if (this.hasAttribute('auto-open') && !onCartPage) {
      const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
      // On add-to-EMPTY the drawer still shows the empty state; the filled content (which
      // contains .cart-form) morphs in on this same cart:update event. Opening now would flash
      // the empty popup, so defer until .cart-form is present. If the cart already had items
      // (dialog already has .cart-form) or the drawer is open, open immediately as before.
      if (dialog && !dialog.open && !dialog.querySelector('.cart-form')) {
        this.#openWhenFilled(dialog);
      } else {
        this.showDialog();
      }
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Opens the drawer once the filled cart content (.cart-form) has morphed into the dialog,
   * so the empty-cart popup never flashes on add-to-empty. Auto-disconnects; a timeout
   * fallback opens anyway so the drawer can never hang closed.
   * @param {HTMLDialogElement} dialog
   */
  #openWhenFilled(dialog) {
    // Mark the dialog so the centered-empty CSS is suppressed for the whole add
    // sequence (the empty layout must never paint on add-to-cart). Removed once open+filled.
    dialog.setAttribute('data-suppress-empty', '');
    let opened = false;
    const open = () => {
      if (opened) return;
      opened = true;
      observer.disconnect();
      clearTimeout(fallback);
      // Only open once the filled content is actually present; if the fallback
      // fires while still empty, open anyway (never hang) but keep suppression
      // so the big centered popup still cannot show.
      this.showDialog();
      // Clear the marker on the next frame, after showModal has painted the
      // filled layout, so the normal (filled) styling takes over cleanly.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => dialog.removeAttribute('data-suppress-empty'));
      });
    };
    const observer = new MutationObserver(() => {
      if (dialog.querySelector('.cart-form')) open();
    });
    observer.observe(dialog, { childList: true, subtree: true });
    const fallback = setTimeout(open, 1000);
    if (dialog.querySelector('.cart-form')) open();
  }

  /**
   * Re-opens the drawer after a discount apply. applyDiscount() dispatches
   * DiscountUpdateEvent then morphSection() re-renders this section from server HTML
   * where the <dialog> is closed, stripping the open/showModal state. Re-assert open on
   * the next frame (after the synchronous in-place morph). The open check runs now
   * (before the morph), so a discount applied on the cart page (drawer already closed)
   * never force-opens the header drawer.
   */
  #handleDiscountUpdate = () => {
    if (!this.refs.dialog?.open) return;
    requestAnimationFrame(() => this.showDialog());
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
